import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface SiteConfig {
  [key: string]: any;
}

export const useSiteConfig = () => {
  const [config, setConfig] = useState<SiteConfig>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSiteConfig();
  }, []);

  const fetchSiteConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('site_config')
        .select('key, value');

      if (error) {
        console.error('Error fetching site config:', error);
        return;
      }

      const configObject = data?.reduce((acc, item) => {
        acc[item.key] = item.value;
        return acc;
      }, {} as SiteConfig) || {};

      setConfig(configObject);
    } catch (error) {
      console.error('Error fetching site config:', error);
    } finally {
      setLoading(false);
    }
  };

  const getConfig = (key: string, defaultValue: any = null) => {
    const value = config[key];
    if (value === undefined || value === null) return defaultValue;

    const unwrap = (val: any): any => {
      if (val === null || val === undefined) return defaultValue;
      if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return val;
      if (typeof val === 'object') {
        // Keep complex objects for logos, social links, and WhatsApp
        if (("url" in val && "alt" in val) || ("enabled" in val && "url" in val) || ("enabled" in val && "number" in val)) return val;
        // Common scalar fields inside objects
        for (const k of ['text', 'value', 'phone', 'email', 'number', 'href']) {
          if (k in val && typeof (val as any)[k] !== 'object') return (val as any)[k];
        }
        // If it's an object with a single primitive value, unwrap it
        const keys = Object.keys(val as any);
        if (keys.length === 1 && typeof (val as any)[keys[0]] !== 'object') {
          return (val as any)[keys[0]];
        }
        // Avoid returning raw objects to be rendered as React children
        return defaultValue;
      }
      return defaultValue;
    };

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          return unwrap(JSON.parse(trimmed));
        } catch {
          // fallthrough to return raw string
        }
      }
      return value;
    }

    return unwrap(value);
  };

  const refetch = () => {
    fetchSiteConfig();
  };

  return { config, loading, getConfig, refetch };
};